import React, { useEffect, useMemo, useState } from 'react';
import { splitFileName, suggestDocumentTitle } from '../utils/suggestDocumentTitle';

export interface BatchRenameRow {
  file: File;
  newName: string;
  documentType?: string;
}

// Kept as an alias — this component replaced the drag-drop-only
// ProjectDropRenameModal, and existing call sites import this type name.
export type ProjectDropFile = BatchRenameRow;

const DOCUMENT_TYPE_OPTIONS = ['Other', 'Quote', 'Binder', 'Policy', 'Loss Run'];
const SOFT_LENGTH_WARNING = 40;

interface BatchRenameModalProps {
  files: File[];
  /** Label for what these files are being uploaded to, e.g. a project title. */
  projectTitle: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (renamed: BatchRenameRow[]) => void;
  /** Show a per-row document-type dropdown (Quote/Binder/Policy/…) and include it in onConfirm rows. */
  showDocumentType?: boolean;
}

/**
 * Single-screen batch rename table: one row per dropped/selected file, each
 * with an editable (auto-suggested) name and optional document-type
 * dropdown. Replaces the old "one modal per file, Next/Skip through them"
 * flow — used by both the drag-and-drop upload path and FileUploader's
 * click-to-upload path so behavior doesn't depend on how files arrived.
 */
const BatchRenameModal: React.FC<BatchRenameModalProps> = ({
  files,
  projectTitle,
  isOpen,
  onCancel,
  onConfirm,
  showDocumentType = false,
}) => {
  const initial = useMemo<BatchRenameRow[]>(
    () => files.map((file) => ({
      file,
      newName: suggestDocumentTitle(file.name),
      documentType: showDocumentType ? 'Other' : undefined,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files]
  );
  const [rows, setRows] = useState<BatchRenameRow[]>(initial);

  // Reset internal state whenever a new batch is opened
  useEffect(() => {
    if (isOpen) setRows(initial);
  }, [isOpen, initial]);

  // Esc to cancel
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onCancel]);

  if (!isOpen || files.length === 0) return null;

  const allNamed = rows.every((r) => r.newName.trim().length > 0);

  const update = (idx: number, patch: Partial<BatchRenameRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleConfirm = () => {
    if (!allNamed) return;
    onConfirm(
      rows.map(({ file, newName, documentType }) => {
        const { ext } = splitFileName(file.name);
        const finalName = newName.trim().endsWith(ext) ? newName.trim() : `${newName.trim()}${ext}`;
        return { file, newName: finalName, documentType };
      })
    );
  };

  // Close only when the mousedown itself starts on the backdrop, not on
  // `click` — a click's target is the nearest common ancestor of the
  // mousedown/mouseup targets, so selecting text in a name field by
  // dragging (mousedown inside the input, mouseup wherever the drag ends)
  // can land a synthetic click on the backdrop even though the whole
  // gesture happened over the input, closing the modal mid-selection.
  // Checking `e.target === e.currentTarget` on mousedown instead means the
  // gesture has to actually START on the backdrop to count as "outside".
  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="batch-rename-title"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border flex items-start justify-between">
          <div>
            <h3 id="batch-rename-title" className="text-base font-semibold text-secondary dark:text-dark-text">
              Upload {files.length} {files.length === 1 ? 'file' : 'files'} to {projectTitle}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Rename and tag each file before upload. Extensions are kept automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel upload"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-3">
            {rows.map((row, idx) => {
              const { ext } = splitFileName(row.file.name);
              const tooLong = row.newName.trim().length > SOFT_LENGTH_WARNING;
              return (
                <li key={`${row.file.name}-${idx}`} className="border border-gray-200 dark:border-dark-border rounded-lg p-3">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 truncate" title={row.file.name}>
                    Original: {row.file.name} · {(row.file.size / 1024).toFixed(0)} KB
                  </div>
                  <div className="flex gap-2">
                    <div className="flex flex-1">
                      <input
                        type="text"
                        value={row.newName}
                        onChange={(e) => update(idx, { newName: e.target.value })}
                        placeholder="Document name"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-l focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      />
                      {ext && (
                        <span className="px-2 py-2 text-xs bg-gray-100 dark:bg-gray-600 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r text-gray-500 dark:text-gray-300 flex items-center">
                          {ext}
                        </span>
                      )}
                    </div>
                    {showDocumentType && (
                      <select
                        value={row.documentType || 'Other'}
                        onChange={(e) => update(idx, { documentType: e.target.value })}
                        className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white flex-shrink-0"
                      >
                        {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  {tooLong && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                      Long titles get truncated in report tables — consider shortening.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {allNamed ? 'Ready to upload' : 'Each file needs a name'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!allNamed}
              className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              Upload {files.length}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchRenameModal;
